import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, TrendingUp, ChevronRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function BillableHoursWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/billable-hours/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching billable hours:', err);
    } finally {
      setLoading(false);
    }
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-green-500';
    if (percent >= 70) return 'bg-blue-500';
    if (percent >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 h-full">
        <div className="flex items-center justify-center h-32">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <h2 className="text-lg font-semibold text-white">Billable Hours</h2>
        </div>
        <button
          onClick={() => navigate('/billable-hours')}
          className="text-sm text-slate-400 hover:text-white flex items-center transition-colors px-3 py-1.5 hover:bg-white/5 rounded-lg"
        >
          View All
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>

      {/* Progress Section */}
      <div className="flex-1 space-y-4">
        {/* Today's Progress */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-400">Today</span>
            <span className="text-white font-medium">
              {stats.actual.today} / {stats.goals.daily_goal}h
            </span>
          </div>
          <div className="h-2.5 bg-slate-700/50 rounded-full overflow-hidden">
            <div
              className={`h-full ${getProgressColor(stats.progress.daily_percent)} transition-all duration-500 rounded-full`}
              style={{ width: `${Math.min(stats.progress.daily_percent, 100)}%` }}
            />
          </div>
        </div>

        {/* Weekly Progress */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-slate-400">This Week</span>
            <span className="text-white font-medium">
              {stats.actual.this_week} / {stats.goals.weekly_goal}h
            </span>
          </div>
          <div className="h-2.5 bg-slate-700/50 rounded-full overflow-hidden">
            <div
              className={`h-full ${getProgressColor(stats.progress.weekly_percent)} transition-all duration-500 rounded-full`}
              style={{ width: `${Math.min(stats.progress.weekly_percent, 100)}%` }}
            />
          </div>
        </div>

        {/* Monthly Stats */}
        <div className="flex justify-between items-center pt-3 border-t border-slate-700/50">
          <div className="text-xs text-slate-400 flex items-center">
            <TrendingUp size={12} className="mr-1 text-emerald-400" />
            {stats.period_info.month_name}
          </div>
          <div className="text-sm">
            <span className="text-white font-semibold">{stats.actual.this_month}</span>
            <span className="text-slate-500"> / {stats.goals.monthly_goal}h</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BillableHoursWidget;
